import { useEffect, useState } from 'react';
import { useNCZIContext } from '../Services/NCZIContext';
import { GetPatientIdentifier } from '../Commands/GetPatientIdentifier';
import useNCZICommands from './useNCZICommands';
import { ExecutedCommand, FinishedCommand } from '../Commands/Command';
import { NCZIJruzPatientIdentification } from '../Interfaces/ncziJruzPatientIdentification';
import { GetPatientSummaryContactInformation } from '../Commands/GetPatientSummaryContactInformation';
import { NCZIPatientSummaryContactInformation } from '../Interfaces/ncziPatientInformations';
import { GetPatientSummaryEDS } from '../Commands/GetPatientSummaryEDS';
import { NCZIEhrExtractEnvelope } from '../Interfaces/ncziTypes';

export default function useNCZI() {
	const {
		patientSummaryContactData,
		setPatientSummaryContactData,
		patientIdentifier,
		setPatientIdentifier,
		patientSummaryData,
		setPatientSummaryData,
	} = useNCZIContext();

	const { commands, execute } = useNCZICommands();

	const [patientIdentifierCommandId, setPatientIdentifierCommandId] = useState<
		string | undefined
	>(undefined);
	const [
		patientSummaryContactInformationCommandId,
		setPatientSummaryContactInformationCommandId,
	] = useState<string | undefined>(undefined);
	const [patientSummaryEDSCommandId, setPatientSummaryEDSCommandId] = useState<
		string | undefined
	>(undefined);
	const [patientExaminationListCommandId, setPatientExaminationListCommandId] =
		useState<string | undefined>(undefined);

	useEffect(() => {
		if (patientIdentifierCommandId && !patientIdentifier) {
			processPatientIdentifierCommandResult(
				commands[patientIdentifierCommandId],
			);
		}
	}, [commands, patientIdentifierCommandId]);

	useEffect(() => {
		if (
			patientSummaryContactInformationCommandId &&
			!patientSummaryContactData
		) {
			const command = commands[patientSummaryContactInformationCommandId];
			if (command && command.state === 'done') {
				const finishedCommand = command as FinishedCommand;
				if (finishedCommand.result) {
					setPatientSummaryContactData(
						finishedCommand.result as NCZIPatientSummaryContactInformation,
					);
				}
			}
		}
	}, [commands, patientSummaryContactInformationCommandId]);

	useEffect(() => {
		if (patientSummaryEDSCommandId && !patientSummaryData) {
			const command = commands[patientSummaryEDSCommandId];
			if (command && command.state === 'done') {
				const finishedCommand = command as FinishedCommand;
				if (finishedCommand.result) {
					setPatientSummaryData(
						finishedCommand.result as NCZIEhrExtractEnvelope,
					);
				}
			}
		}
	}, [commands, patientSummaryEDSCommandId]);

	const fetchPatientIdentifier = async (birthNumber: string) => {
		return await obtainPatientIdentifier(birthNumber);
	};

	const fetchPatientSummaryContactData = async (
		patientIdentifier: string,
		patientId?: string,
	) => {
		const command = await obtainPatientSummaryContactData(
			patientIdentifier,
			patientId,
		);
		setPatientSummaryContactData(null);
		return command;
	};

	const fetchPatientSummaryData = async (patientIdentifier: string) => {
		const command = await obtainPatientSummaryData(patientIdentifier);
		setPatientSummaryData(null);
		return command;
	};

	const obtainPatientIdentifier = async (birthNumber: string) => {
		const commandGetPatientIdentifier = GetPatientIdentifier({
			birthNumber,
		});

		const result = await execute(commandGetPatientIdentifier);

		if (!('error' in result && result.error)) {
			setPatientIdentifierCommandId(result.id);
			processPatientIdentifierCommandResult(result);
		}

		return result;
	};

	const processPatientIdentifierCommandResult = (
		result: ExecutedCommand | FinishedCommand,
	) => {
		if (result && result.state === 'done' && 'result' in result) {
			const finishedCommand = result as FinishedCommand;
			if (finishedCommand.result) {
				const JRUZResponse =
					finishedCommand.result as NCZIJruzPatientIdentification;
				setPatientIdentifier(JRUZResponse.patientCode);
			}
		}
	};

	const obtainPatientSummaryContactData = async (
		patientCode: string,
		patientId?: string,
	) => {
		const commandGetPatientSummaryContactInformation =
			GetPatientSummaryContactInformation({
				patientCode,
				patientId,
			});

		const result = await execute(commandGetPatientSummaryContactInformation);
		if (!('error' in result && result.error)) {
			setPatientSummaryContactInformationCommandId(result.id);
		}

		return result;
	};

	const obtainPatientSummaryData = async (patientCode: string) => {
		const commandGetPatientSummaryContactInformation = GetPatientSummaryEDS({
			patientCode,
		});

		const result = await execute(commandGetPatientSummaryContactInformation);
		if (!('error' in result && result.error)) {
			setPatientSummaryEDSCommandId(result.id);
		}

		return result;
	};

	return {
		fetchPatientSummaryContactData,
		fetchPatientIdentifier,
		fetchPatientSummaryData,
	};
}
